import {
  UICorePlugin,
  type AttributeMap,
  type SupportedVersion
} from '@clappr/core'
import type {Options} from 'common/types/options'
import {Events} from 'common/types/events'
import {waitTransitionEnd} from 'utils/transition'
import type {MediaControl} from 'common/plugins/media-control'
import styles from './styles.scss'

export class Notifier<T extends Options> extends UICorePlugin<T> {
  public static SHOW_DURATION = 3e3

  private rejectMessage?: (reason?: any) => void

  public get name(): string {
    return 'notifier'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get attributes(): AttributeMap {
    return {
      class: `${styles.notifier}`
    }
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  public async showMessage(
    text: string,
    className?: string,
    position: 'top' | 'bottom' = 'top'
  ): Promise<void> {
    this.rejectMessage?.('rejected')

    const el = document.createElement('div')
    const $item = $(el)
      .toggleClass(styles.message, true)
      .toggleClass(styles[position], true)
      .toggleClass(className as string, true)
      .text(text)

    try {
      this.$el.append($item)

      await new Promise((resolve, reject) => {
        this.rejectMessage = reject
        setTimeout(resolve, 0)
      })
      $item.toggleClass(styles.visible, true)
      await new Promise((resolve, reject) => {
        this.rejectMessage = reject
        setTimeout(resolve, Notifier.SHOW_DURATION)
      })

      const transitionEnd = waitTransitionEnd(el, 'opacity')

      this.rejectMessage = transitionEnd.reject
      $item.toggleClass(styles.visible, false)
      await transitionEnd.promise
    } catch (error) {
      if (error !== 'rejected') throw error
    } finally {
      delete this.rejectMessage
      $item.remove()
    }
  }

  public render(): this {
    if (this.mediaControl) {
      this.core.$el.append(this.el)
    }

    return this
  }
}
